import {Player} from "@/types";

export function ifnull(value: any, fallback: any = '') {
    if (value === null || typeof value === 'undefined') {
        return fallback;
    }
    return value;
}

/**
 * Humanise a URL slug (or uid) into a readable label, e.g. "ndl-ladder" -> "Ndl Ladder".
 * Used for SSR-safe fallback page titles before the event data has loaded client-side.
 */
export function humanizeSlug(slug?: string | null): string {
    if (!slug) return '';
    return slug
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert a `<input type="datetime-local">` value (a naive wall-clock string in
 * the browser's local TZ, e.g. "2026-05-31T09:00") to a UTC ISO string suitable
 * for sending to the backend (e.g. "2026-05-31T05:00:00.000Z" for a Dubai user).
 *
 * Without this, the backend stores the wall-clock as if it were UTC, and the
 * public page later renders it shifted by the viewer's TZ offset (so "09:00"
 * entered in Dubai becomes "1:00 PM" on display).
 */
export function localInputToUtcIso(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
}

/**
 * Inverse of `localInputToUtcIso`. Takes a UTC ISO string from the backend and
 * returns a string formatted for a `<input type="datetime-local">` in the
 * browser's local TZ. Padding-safe so values like 9:05 don't lose the leading
 * zero.
 */
export function utcIsoToLocalInput(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function maybeUndefinedDefaultKey(value?: any) {
    return value ? [String(value)] : undefined;
}

export function maybeInt(value: string) {
    if (value === '') {
        return null;
    }
    return parseInt(value, 10);
}

export function ordinal(n: number) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function ensureObject(obj: any, fallback: any = {}) {
    if (Array.isArray(obj)) {
        return fallback;
    }
    return obj;
}

export function thumbUrl(player?: Player) {
    return player?.profile_thumb_url || player?.profile_image_url;
}

export function cutTimeLabel(entryEndsAt?: string | null, startsAt?: string | null, fallback = 'Registration Open') {
    const now = Date.now();
    const entryEnd = entryEndsAt ? new Date(entryEndsAt).getTime() : null;
    const start = startsAt ? new Date(startsAt).getTime() : null;

    const deadline = (entryEnd && entryEnd > now)
        ? entryEnd
        : (start && start > now) ? start : null;

    if (deadline === null) {
        if (entryEnd !== null || start !== null) return 'Registration Closed';
        return fallback;
    }

    const diff = deadline - now;
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    if (hours > 0 || days > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
    parts.push(`${seconds}s`);
    return `Closes in ${parts.join(' ')}`;
}

export function durationUntil(iso?: string | null): string | null {
    if (!iso) return null;
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return null;
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    if (hours > 0 || days > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
}

export function participantsLabel(approved: number, max: number | null | undefined, participantsType?: number | null) {
    const isTeams = Number(participantsType) === 2;
    const unit = isTeams ? (approved === 1 ? 'team' : 'teams') : (approved === 1 ? 'player' : 'players');
    const base = max ? `${approved} ${unit} / ${max}` : `${approved} ${unit}`;
    return isTeams ? `${base} (${approved * 2} players)` : base;
}
